/**
 * HOPPN LAYOUT OVERRIDES
 * Version 1.3
 *
 * Customizes product and skeleton grid layouts,
 * product title sizes, and maximum title lines
 * inside Hoppn's open Shadow DOM.
 */

(() => {
  "use strict";

  // =====================================================
  // CONFIGURATION
  // =====================================================

  const CONFIG = {
    desktop: {
      columns: 5,
      columnGap: 10,
      rowGap: 10,
      titleFontSize: 12,
      titleMaxLines: 4,
    },

    mobile: {
      columns: 2,
      columnGap: 4,
      rowGap: 8,
      titleFontSize: 12,
      titleMaxLines: 4,
    },

    // Desktop begins at this viewport width (px)
    breakpoint: 768,

    // Enable console logging for debugging
    debug: false,
  };

  // =====================================================
  // CSS SELECTORS
  // =====================================================

  /*
   * Target only actual grid containers.
   * Skeleton grids automatically share product settings.
   */

  const GRID_SELECTORS = [
    ".ics-product-list-standard",
    ".ics-skeleton-list-standard",
    ".ics-skeleton-list-smart",
  ];

  const GRID_SELECTOR = GRID_SELECTORS.join(", ");

  // Hoppn product title selector
  const TITLE_SELECTOR =
    ".ics-product-list-standard-card__header .title";

  const STYLE_ID = "hoppn-layout-overrides";

  const observedRoots = new WeakSet();

  // =====================================================
  // CSS GENERATION
  // =====================================================

  function generateCSS() {
    const { desktop, mobile, breakpoint } = CONFIG;

    return `
      /* MOBILE PRODUCT + SKELETON GRIDS */

      ${GRID_SELECTOR} {
        grid-template-columns:
          repeat(${mobile.columns}, minmax(0, 1fr)) !important;

        column-gap: ${mobile.columnGap}px !important;
        row-gap: ${mobile.rowGap}px !important;
      }

      /* MOBILE PRODUCT TITLES */

      ${TITLE_SELECTOR} {
        font-size: ${mobile.titleFontSize}px !important;

        white-space: normal !important;
        display: -webkit-box !important;
        -webkit-box-orient: vertical !important;
        -webkit-line-clamp: ${mobile.titleMaxLines} !important;

        overflow: hidden !important;
        text-overflow: ellipsis !important;
      }

      /* DESKTOP PRODUCT + SKELETON GRIDS */

      @media (min-width: ${breakpoint}px) {
        ${GRID_SELECTOR} {
          grid-template-columns:
            repeat(${desktop.columns}, minmax(0, 1fr)) !important;

          column-gap: ${desktop.columnGap}px !important;
          row-gap: ${desktop.rowGap}px !important;
        }

        /* DESKTOP PRODUCT TITLES */

        ${TITLE_SELECTOR} {
          font-size: ${desktop.titleFontSize}px !important;
          -webkit-line-clamp: ${desktop.titleMaxLines} !important;
        }
      }
    `;
  }

  const CSS = generateCSS();

  // =====================================================
  // STYLE INJECTION
  // =====================================================

  function injectStyles(root) {
    if (!(root instanceof ShadowRoot)) return;

    // Only inject styles into roots containing Hoppn elements.
    if (!root.querySelector(GRID_SELECTOR)) return;

    let style = root.getElementById(STYLE_ID);

    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = CSS;

      root.appendChild(style);

      if (CONFIG.debug) {
        console.log("[Hoppn] Layout override applied.");
      }
    } else if (style.textContent !== CSS) {
      style.textContent = CSS;
    }
  }

  // =====================================================
  // SHADOW DOM DISCOVERY
  // =====================================================

  function discover(root) {
    root.querySelectorAll("*").forEach((element) => {
      if (element.shadowRoot) {
        observe(element.shadowRoot);
      }
    });
  }

  // =====================================================
  // DOM OBSERVATION
  // =====================================================

  function observe(root) {
    if (observedRoots.has(root)) return;

    observedRoots.add(root);

    if (root instanceof ShadowRoot) {
      injectStyles(root);
    }

    // Find nested open shadow roots.
    discover(root);

    const observer = new MutationObserver((mutations) => {
      if (root instanceof ShadowRoot) {
        injectStyles(root);
      }

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.shadowRoot) {
              observe(node.shadowRoot);
            }

            discover(node);
          }
        }
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
    });
  }

  // =====================================================
  // INITIALIZATION
  // =====================================================

  function initialize() {
    observe(document);

    // Catch shadow roots attached after page loading.
    setInterval(() => {
      discover(document);
    }, 2000);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();
